import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://leadgen-api.anassk.workers.dev';

function LoginScreen({ onLogin, onSignup, isLoading }) {
  const [mode, setMode] = useState('landing'); // 'landing', 'login', 'signup', 'pending', 'forgot'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
    phone: '',
    website: '',
    address: ''
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await onLogin(formData.email, formData.password);
    if (!result.success) {
      setError(result.message || 'Invalid credentials');
    }
    setLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await onSignup({
      name: formData.name,
      email: formData.email,
      company: formData.company,
      phone: formData.phone,
      website: formData.website,
      address: formData.address
    });
    if (result.success) {
      setMode('pending');
    } else {
      setError(result.message || 'Signup failed');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await res.json();
      if (data.success) {
        setMessage('Check your email for a password reset link.');
      } else {
        setError(data.message || 'Something went wrong');
      }
    } catch (e) {
      setError('Connection error');
    }
    setLoading(false);
  };

  // Pending approval screen
  if (mode === 'pending') {
    return (
      <div className="landing-page">
        <div className="landing-container">
          <div className="pending-card">
            <div className="pending-icon">📧</div>
            <h2>Application Submitted!</h2>
            <p>Thank you for signing up. Your application is being reviewed.</p>
            <p className="pending-note">You'll receive your login credentials via email once approved.</p>
            <button className="btn-secondary" onClick={() => setMode('login')}>
              Already have credentials? Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Forgot password form
  if (mode === 'forgot') {
    return (
      <div className="landing-page">
        <div className="landing-container">
          <div className="auth-card">
            <div className="auth-logo">
              <img src="/images/logo.webp" alt="Logo" />
            </div>
            <h2>Reset Password</h2>
            <p className="auth-subtitle">Enter your email to receive a reset link</p>

            <form onSubmit={handleForgotPassword} className="auth-form">
              {error && <div className="auth-error">{error}</div>}
              {message && <div className="auth-success">{message}</div>}

              <div className="auth-field">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  required
                  autoFocus
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <div className="auth-switch">
              <span>Remember your password?</span>
              <button onClick={() => setMode('login')}>Sign In</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Login form
  if (mode === 'login') {
    return (
      <div className="landing-page">
        <div className="landing-container">
          <div className="auth-card">
            <div className="auth-logo">
              <img src="/images/logo.webp" alt="Logo" />
            </div>
            <h2>Welcome Back</h2>
            <p className="auth-subtitle">Sign in to access your leads</p>

            <form onSubmit={handleLogin} className="auth-form">
              {error && <div className="auth-error">{error}</div>}

              <div className="auth-field">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  required
                  autoFocus
                />
              </div>

              <div className="auth-field">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <button type="button" className="btn-link" onClick={() => setMode('forgot')}>
                Forgot password?
              </button>
            </form>

            <div className="auth-switch">
              <span>Don't have an account?</span>
              <button onClick={() => setMode('signup')}>Request Access</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Signup form
  if (mode === 'signup') {
    return (
      <div className="landing-page">
        <div className="landing-container">
          <div className="auth-card signup-card">
            <button className="back-btn" onClick={() => setMode('landing')}>← Back</button>
            <h2>Request Access</h2>
            <p className="auth-subtitle">Tell us about your business</p>

            <form onSubmit={handleSignup} className="auth-form">
              {error && <div className="auth-error">{error}</div>}

              <div className="auth-row">
                <div className="auth-field">
                  <label>Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div className="auth-field">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    required
                  />
                </div>
              </div>

              <div className="auth-field">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="ABC Roofing & Windows"
                  required
                />
              </div>

              <div className="auth-row">
                <div className="auth-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="auth-field">
                  <label>Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://yourcompany.com"
                  />
                </div>
              </div>

              <div className="auth-field">
                <label>Business Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Main St, City, State"
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>

            <div className="auth-switch">
              <span>Already have an account?</span>
              <button onClick={() => setMode('login')}>Sign In</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Landing page
  return (
    <div className="landing-page">
      {/* Hero Section */}
      <div className="hero-section">
        <nav className="landing-nav">
          <div className="nav-logo">
            <img src="/images/logo.webp" alt="Logo" />
            <span>LeadBlast Pro</span>
          </div>
          <div className="nav-buttons">
            <button className="btn-ghost" onClick={() => setMode('login')}>Sign In</button>
            <button className="btn-primary-sm" onClick={() => setMode('signup')}>Get Started</button>
          </div>
        </nav>

        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge">For Home Service Professionals</div>
            <h1>Target High-Value Homeowners.<br />Close More Deals.</h1>
            <p className="hero-subtitle">
              Draw a rectangle on the map. Get owner names, addresses, and property data instantly.
              Export print-ready flyers with personalized names. Perfect for roofers, window installers,
              HVAC, solar, and more.
            </p>
            <div className="hero-cta">
              <button className="btn-primary-lg" onClick={() => setMode('signup')}>
                Request Free Access
              </button>
              <button className="btn-secondary-lg" onClick={() => setMode('login')}>
                Sign In
              </button>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <strong>346K+</strong>
                <span>Properties</span>
              </div>
              <div className="stat">
                <strong>Owner Data</strong>
                <span>Names & Addresses</span>
              </div>
              <div className="stat">
                <strong>Instant</strong>
                <span>CSV Export</span>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <img src="/images/hero-map.webp" alt="Lead Generation Map" />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <h2>How It Works</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">1</div>
            <img src="/images/onboard-search.webp" alt="Search" className="feature-img" />
            <h3>Search Any Address</h3>
            <p>Type any address or navigate the map to find your target neighborhood.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">2</div>
            <img src="/images/onboard-draw.webp" alt="Draw" className="feature-img" />
            <h3>Draw Your Territory</h3>
            <p>Use rectangle or circle tools to select properties. Get instant results.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">3</div>
            <img src="/images/hero-flyers.webp" alt="Export" className="feature-img" />
            <h3>Export & Print</h3>
            <p>Download CSV with owner names. Create personalized flyers. Mail them out.</p>
          </div>
        </div>
      </div>

      {/* Industries Section */}
      <div className="industries-section">
        <h2>Built For Home Service Pros</h2>
        <div className="industries-grid">
          <div className="industry-tag">Roofing</div>
          <div className="industry-tag">Windows & Doors</div>
          <div className="industry-tag">Solar</div>
          <div className="industry-tag">HVAC</div>
          <div className="industry-tag">Siding</div>
          <div className="industry-tag">Gutters</div>
          <div className="industry-tag">Landscaping</div>
          <div className="industry-tag">Painting</div>
          <div className="industry-tag">Fencing</div>
          <div className="industry-tag">Concrete</div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <h2>Ready to Blast Your Territory?</h2>
        <p>Get access to thousands of homeowner leads with property data.</p>
        <button className="btn-primary-lg" onClick={() => setMode('signup')}>
          Request Access Now
        </button>
      </div>

      {/* Footer */}
      <footer className="landing-footer">
        <p>LeadBlast Pro - High-Ticket Lead Generation for Home Services</p>
      </footer>
    </div>
  );
}

export default LoginScreen;
